import { useRef, useState } from "react";
import { Camera, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const MAX_PHOTOS = 10;
const MAX_WIDTH = 1024;
const JPEG_QUALITY = 0.72;
const MAX_COMPRESSED_BYTES = 800_000; // sync with backend

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = (e) => resolve(e.target.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });
}

async function compressImage(file) {
  const dataUrl = await fileToDataUrl(file);
  const img = await loadImage(dataUrl);
  const ratio = Math.min(1, MAX_WIDTH / img.width);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * ratio);
  canvas.height = Math.round(img.height * ratio);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  // Try progressively lower quality until under limit
  let quality = JPEG_QUALITY;
  let out = canvas.toDataURL("image/jpeg", quality);
  while (out.length > MAX_COMPRESSED_BYTES && quality > 0.35) {
    quality -= 0.1;
    out = canvas.toDataURL("image/jpeg", quality);
  }
  return out;
}

export default function PhotoUpload({ value = [], onChange, onPreview }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const remaining = MAX_PHOTOS - value.length;
    if (remaining <= 0) {
      toast.error(`Maksimal ${MAX_PHOTOS} foto per kunjungan`);
      return;
    }
    const toProcess = Array.from(files).slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(`Hanya ${remaining} foto pertama yang diproses (batas ${MAX_PHOTOS}).`);
    }

    setBusy(true);
    const compressed = [];
    for (const file of toProcess) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} bukan file gambar`);
        continue;
      }
      try {
        const dataUrl = await compressImage(file);
        if (dataUrl.length > MAX_COMPRESSED_BYTES) {
          toast.error(`${file.name} terlalu besar setelah kompresi`);
          continue;
        }
        compressed.push(dataUrl);
      } catch (e) {
        toast.error(`Gagal memproses ${file.name}`);
      }
    }
    setBusy(false);
    if (compressed.length > 0) {
      onChange([...value, ...compressed]);
      toast.success(`${compressed.length} foto ditambahkan`);
    }
  };

  const removeAt = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const canAdd = value.length < MAX_PHOTOS;

  return (
    <div className="space-y-3">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        data-testid="photo-file-input"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
        {value.map((src, idx) => (
          <div
            key={idx}
            className="relative aspect-square border border-black bg-neutral-100 overflow-hidden group"
            data-testid={`photo-thumb-${idx}`}
          >
            <button
              type="button"
              onClick={() => onPreview && onPreview(idx)}
              className="w-full h-full block"
              aria-label={`Lihat foto ${idx + 1}`}
            >
              <img src={src} alt={`foto-${idx + 1}`} className="w-full h-full object-cover" />
            </button>
            <button
              type="button"
              onClick={() => removeAt(idx)}
              data-testid={`photo-remove-${idx}`}
              className="absolute top-1 right-1 bg-black text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label={`Hapus foto ${idx + 1}`}
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {canAdd && (
          <button
            type="button"
            data-testid="photo-add-btn"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="aspect-square border border-dashed border-black hover:bg-black hover:text-white transition-colors duration-150 flex flex-col items-center justify-center gap-1 disabled:opacity-50"
          >
            {busy ? (
              <span className="label-overline">Memproses...</span>
            ) : (
              <>
                <Camera size={20} />
                <span className="label-overline">Tambah</span>
              </>
            )}
          </button>
        )}
      </div>

      <div className="text-[0.65rem] text-neutral-500 flex items-center gap-1">
        <ImageIcon size={11} />
        <span>{value.length}/{MAX_PHOTOS} foto — otomatis dikompres (JPEG maks 1024px)</span>
      </div>
    </div>
  );
}
